import { useProcess, useSelect } from '~/store'
import { DocumentGenerationError } from '@st/sdk'
import { Button, Dialog, DialogButtons, Modal, Spinner, TagChip, TagChipColor } from '@st/theme'
import { inflect } from '@st/util/inflect'
import { SortDirection, SortState } from '@st/util/sort'
import { formatISO8601DateTime } from '@st/util/time'
import { match, P } from '@st/pattern'
import { selKarbonConnected } from '~/features/st-organizations/st-organization-module'
import {
  DocumentItem,
  DownloadListSortColumn,
  closeDownloadFolderDialog,
  selDownloadableDocumentList,
  selDownloadAdditionalDocuments,
  STDownloadFolderMessage,
  STDownloadFolderModuleState
} from './st-download-folder-module'
import { formatDocumentWarning, selDocumentNameById } from './document-warnings'

export function STDownloadFolderDialog() {
  const stDownloadFolder = useProcess('stDownloadFolder')
  const state = useSelect('stDownloadFolder', (s) => s)
  const karbonConnected = useSelect('stOrganization', selKarbonConnected)

  function onCloseDialog() {
    stDownloadFolder.send(closeDownloadFolderDialog())
  }

  return match(state)
    .with({ status: 'idle' }, () => null)
    .with({ status: 'loading' }, () => <LoadingDialog onClose={onCloseDialog} />)
    .with({ status: 'loadFailed' }, () => (
      <LoadFailedDialog onClose={onCloseDialog} send={stDownloadFolder.send} />
    ))
    .with({ status: 'loaded' }, (state) => (
      <LoadedDialog
        state={state}
        karbonConnected={karbonConnected}
        onClose={onCloseDialog}
        send={stDownloadFolder.send}
      />
    ))
    .exhaustive()
}

function LoadingDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog
      title="Export binder"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={true} leadingIcon={<Spinner className="size-4" />}>
            Loading
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex items-center justify-center py-16">
        <Spinner className="size-8 fill-blue-500 text-blue-100" />
      </div>
    </Dialog>
  )
}

function LoadFailedDialog({
  onClose,
  send
}: {
  onClose: () => void
  send: (message: STDownloadFolderMessage) => void
}) {
  return (
    <Dialog
      title="Export binder"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => send({ type: 'retryLoad' })}>
            Try again
          </Button>
        </DialogButtons>
      }
    >
      <p className="py-8 text-center text-base text-gray-700">
        The binder could not be loaded. Please try again.
      </p>
    </Dialog>
  )
}

type LoadedState = Extract<STDownloadFolderModuleState, { status: 'loaded' }>

function LoadedDialog({
  state,
  karbonConnected,
  onClose,
  send
}: {
  state: LoadedState
  karbonConnected: boolean
  onClose: () => void
  send: (message: STDownloadFolderMessage) => void
}) {
  const items = selDownloadableDocumentList(state)
  const documentNameById = selDocumentNameById(items.map((item) => item.document))
  const download = state.download
  const busy = download.status != 'idle'
  return (
    <>
      <Dialog
        title="Export binder"
        className="sm:max-w-6xl"
        buttons={
          <DialogButtons>
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={busy}
              leadingIcon={download.status == 'generatingZip' && <Spinner className="size-4" />}
              onClick={() => send({ type: 'downloadZip' })}
            >
              {download.status == 'generatingZip' ? 'Generating zip' : 'Download zip'}
            </Button>
            <WorkpaperButton download={download} busy={busy} destination="download" send={send} />
            {karbonConnected && (
              <WorkpaperButton download={download} busy={busy} destination="karbon" send={send} />
            )}
          </DialogButtons>
        }
      >
        <FileDownloadsTable
          sortState={state.sortState}
          items={items}
          selectedDocumentIds={state.selectedDocumentIds}
          allSelected={state.selectedDocumentIds.length == state.downloadState?.documents.length}
          send={send}
          documentNameById={documentNameById}
        />
      </Dialog>
      {match(download.status)
        .with(P.union('downloadAdditionalDocuments', 'downloadingAdditionalDocumentsZip'), () => (
          <AdditionalDocumentsDialog state={state} send={send} />
        ))
        .otherwise(() => null)}
    </>
  )
}

function WorkpaperButton({
  download,
  busy,
  destination,
  send
}: {
  download: LoadedState['download']
  busy: boolean
  destination: Extract<STDownloadFolderMessage, { type: 'downloadPdfWorkpaper' }>['destination']
  send: (message: STDownloadFolderMessage) => void
}) {
  const active = download.status == 'generatingPdfWorkpaper' && download.destination == destination
  const label = match(destination)
    .with('download', () => (active ? 'Generating PDF workpaper' : 'Download PDF workpaper'))
    .with('karbon', () =>
      active ? 'Sending PDF workpaper to Karbon' : 'Send PDF workpaper to Karbon'
    )
    .exhaustive()

  return (
    <Button
      variant="primary"
      disabled={busy}
      leadingIcon={active && <Spinner className="size-4" />}
      onClick={() => send({ type: 'downloadPdfWorkpaper', destination })}
    >
      {label}
    </Button>
  )
}

function AdditionalDocumentsDialog({
  state,
  send
}: {
  state: LoadedState
  send: (message: STDownloadFolderMessage) => void
}) {
  const download = state.download
  if (
    download.status != 'downloadAdditionalDocuments' &&
    download.status != 'downloadingAdditionalDocumentsZip'
  ) {
    return null
  }
  const downloadingZip = download.status == 'downloadingAdditionalDocumentsZip'
  return (
    <Modal isOpen={true}>
      <Dialog
        title="Download remaining documents"
        subtitle={`${download.errors.length} ${inflect(
          download.errors.length,
          'document'
        )} could not be included in the workpaper. You can download a zip below.`}
        buttons={
          <DialogButtons>
            <Button
              variant="subtle"
              onClick={() => send({ type: 'closeDownloadAdditionalDocuments' })}
            >
              Close
            </Button>
            <Button
              variant="primary"
              disabled={downloadingZip}
              onClick={() => send({ type: 'downloadAdditionalDocumentsZip' })}
            >
              {downloadingZip ? 'Downloading' : 'Download'} {download.errors.length} remaining{' '}
              {inflect(download.errors.length, 'document')}
            </Button>
          </DialogButtons>
        }
      >
        <ul className="list-disc">
          {selDownloadAdditionalDocuments(state).map((item) => (
            <li
              key={item.document.id}
              className="flex flex-row items-center gap-2 text-base text-gray-900"
            >
              <div className="flex flex-row items-center gap-2">{item.document.name}</div>
              <TagChip
                className="mt-1"
                color={match<DocumentGenerationError['kind'], TagChipColor>(item.error.kind)
                  .with('unsupported_mime_type', () => 'gray')
                  .with('password_protected', () => 'red')
                  .with('unknown', () => 'red')
                  .exhaustive()}
              >
                {formatError(item.error)}
              </TagChip>
            </li>
          ))}
        </ul>
      </Dialog>
    </Modal>
  )
}

function FileDownloadsTable({
  sortState,
  items,
  selectedDocumentIds,
  allSelected,
  send,
  documentNameById
}: {
  sortState: SortState<DownloadListSortColumn> | undefined
  items: DocumentItem[]
  selectedDocumentIds: string[]
  allSelected: boolean
  send: (message: STDownloadFolderMessage) => void
  documentNameById: Record<string, string>
}) {
  return (
    <table className="w-full table-fixed divide-y divide-gray-300">
      <thead>
        <tr>
          <th className="w-8">
            <input
              type="checkbox"
              className="h-3 w-3"
              checked={allSelected}
              onChange={() => send({ type: 'toggleSelectAll' })}
            />
          </th>
          <th
            className="cursor-pointer px-2 py-2 text-left text-xs font-normal uppercase text-gray-500"
            onClick={() => send({ type: 'toggleSort', column: 'name' })}
          >
            File name {sortState?.column == 'name' && <SortArrow direction={sortState.direction} />}
          </th>
          <th className="w-32 py-2 text-left text-xs font-normal uppercase text-gray-500">
            Category
          </th>
          <th
            className="w-52 cursor-pointer py-2 text-left text-xs font-normal uppercase text-gray-500"
            onClick={() => send({ type: 'toggleSort', column: 'uploadedAt' })}
          >
            Uploaded at
            {sortState?.column == 'uploadedAt' && <SortArrow direction={sortState.direction} />}
          </th>
          <th
            className="w-32 cursor-pointer py-2 text-left text-xs font-normal uppercase text-gray-500"
            onClick={() => send({ type: 'toggleSort', column: 'exportedAt' })}
          >
            Status{' '}
            {sortState?.column == 'exportedAt' && <SortArrow direction={sortState.direction} />}
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 overflow-y-auto">
        {items.map((item) => (
          <tr key={item.document.id}>
            <td className="text-center">
              <input
                type="checkbox"
                className="h-3 w-3"
                checked={selectedDocumentIds.includes(item.document.id)}
                onChange={() => send({ type: 'toggleSelection', documentId: item.document.id })}
              />
            </td>
            <td className="text-nowrap px-2 py-2 text-base text-gray-900">
              <div className="flex grow flex-row overflow-hidden text-ellipsis text-nowrap">
                {item.document.name}
                {item.warnings.map((warning, index) => (
                  <div key={index} className="ml-4 text-sm text-orange-800">
                    {formatDocumentWarning(warning, { documentNameById })}
                  </div>
                ))}
              </div>
            </td>
            <td className="py-2 text-base text-gray-700">{item.documentType.name}</td>
            <td className="py-2 text-base text-gray-700">
              {item.document.uploadedAt && formatISO8601DateTime(item.document.uploadedAt)}
            </td>
            <td>
              <div className="flex flex-row">
                {item.document.exportedAt ? <TagChip color="gray">Exported</TagChip> : null}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function SortArrow({ direction }: { direction: SortDirection }) {
  return <span className="ml-1 text-xs text-gray-500">{direction == 'asc' ? '▲' : '▼'}</span>
}

function formatError(error: DocumentGenerationError) {
  return match(error.kind)
    .with('password_protected', () => 'Password protected')
    .with('unsupported_mime_type', () => 'Unsupported file type')
    .with('unknown', () => 'Unknown error')
    .exhaustive()
}
